import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, Section, Pill, Stat, fmt } from './ui';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';

const MODE_TONE = { blocked: 'low', redacted: 'warning', full: 'high' };
const MODE_HELP = { blocked: 'Nothing leaves', redacted: 'Names (Jarvis), emails, phones, dates, ID & account numbers stripped first', full: 'Full content (needed for documents / audio)' };

const AiTools = ({ tools, onSet }) => (
  <Section title="AI tools — what may leave DerwentAI" testId="egress-ai-tools">
    <p className="-mt-2 mb-3 text-xs text-slate-500">Every AI request passes this gate. Blocked by default. Approving sends data to overseas AI providers through the Emergent AI gateway.</p>
    <div className="divide-y divide-slate-100">
      {tools.map((t) => (
        <div key={t.tool} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`egress-tool-${t.tool}`}>
          <div className="min-w-[220px] flex-1">
            <div className="text-sm font-semibold text-slate-800">{t.label}</div>
            <div className="text-[11px] text-slate-500">{MODE_HELP[t.mode]}{t.needs_content ? ' · reads documents or audio' : ''}</div>
          </div>
          <Pill tone={MODE_TONE[t.mode]}>{t.mode}</Pill>
          <select className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={t.mode} onChange={(e) => onSet(t, e.target.value)} data-testid={`egress-mode-${t.tool}`}>
            <option value="blocked">Blocked</option>
            <option value="redacted">Allow, strip identity</option>
            <option value="full">Allow full content</option>
          </select>
        </div>
      ))}
    </div>
  </Section>
);

const Destinations = ({ rows, onApprove }) => (
  <Section title="Outbound destinations (every network call)" testId="egress-destinations">
    {!rows.length ? <p className="text-xs text-slate-500">No outbound calls recorded yet.</p> : (
      <table className="w-full text-left text-xs">
        <thead className="text-slate-500"><tr><th className="py-1.5">Destination</th><th>Purpose</th><th>Calls</th><th>Last</th><th>Status</th><th /></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((d) => (
            <tr key={d.host} data-testid={`egress-host-${d.host}`}>
              <td className="py-2 font-mono">{d.host}<div className="font-sans text-[10px] text-slate-400">{d.callers.join(', ')}</div></td>
              <td>{d.label || '—'}</td><td className="tabular-nums">{d.count}</td><td>{fmt(d.last_at)}</td>
              <td>{d.approved ? <Pill tone="info">approved</Pill> : <Pill tone="critical">unapproved</Pill>}</td>
              <td className="text-right">{!d.label && <button type="button" className="text-[11px] font-semibold text-slate-600 underline" onClick={() => onApprove(d.host, !d.approved)} data-testid={`egress-approve-${d.host}`}>{d.approved ? 'Remove approval' : 'Approve'}</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Section>
);

export const DataTransfersTab = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${API}/api/security/egress`).then((r) => setD(r.data)).catch(() => toast.error('Could not load data transfers')), []);
  useEffect(() => { load(); }, [load]);
  const [ask, setAsk] = useState(null);
  const setMode = (t, mode) => (mode === 'blocked' ? save(t, mode) : setAsk({ t, mode }));
  const save = async (t, mode) => {
    try { await axios.put(`${API}/api/security/egress/ai`, { tool: t.tool, mode }); toast.success('AI data policy updated'); load(); } catch { toast.error('Could not update'); }
  };
  const approve = async (host, approved) => { try { await axios.put(`${API}/api/security/egress/hosts`, { host, approved }); load(); } catch { toast.error('Could not update'); } };
  if (!d) return <div className="py-10 text-center text-sm text-slate-400">Loading…</div>;
  const s = d.stats_24h;
  return (
    <div className="space-y-5" data-testid="data-transfers-tab">
      <div className="grid gap-4 sm:grid-cols-4">
        <Stat label="AI requests sent (24h)" value={s.ai_sent} testId="egress-stat-sent" />
        <Stat label="AI requests blocked (24h)" value={s.ai_blocked} testId="egress-stat-blocked" />
        <Stat label="Identity items stripped (24h)" value={s.redactions} testId="egress-stat-redactions" />
        <Stat label="Unapproved destinations" value={s.unapproved_hosts} tone={s.unapproved_hosts ? 'text-rose-600' : undefined} testId="egress-stat-unapproved" />
      </div>
      <AiTools tools={d.ai_tools} onSet={setMode} />
      <AlertDialog open={!!ask} onOpenChange={(v) => !v && setAsk(null)}>
        <AlertDialogContent data-testid="egress-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Allow data to leave DerwentAI?</AlertDialogTitle>
            <AlertDialogDescription>"{ask?.t.label}" will send {ask?.mode === 'full' ? 'full client content' : 'de-identified data'} to overseas AI providers through the Emergent AI gateway. This is logged and you can switch it off at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="egress-confirm-cancel">Keep blocked</AlertDialogCancel>
            <AlertDialogAction onClick={() => { save(ask.t, ask.mode); setAsk(null); }} data-testid="egress-confirm-allow">Allow</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Destinations rows={d.destinations} onApprove={approve} />
      <Section title="AI request log (no content is stored)" testId="egress-ai-log">
        <ul className="divide-y divide-slate-100 text-xs">{d.ai_log.map((x) => (
          <li key={x.id} className="flex flex-wrap items-center gap-2 py-2"><Pill tone={x.outcome === 'blocked' ? 'low' : 'warning'}>{x.outcome}</Pill><span className="flex-1 text-slate-700">{x.tool_label} · {x.provider}{x.model ? ` / ${x.model}` : ''} · {x.mode}{x.redactions ? ` · ${x.redactions} stripped` : ''}{x.reason ? ` · ${x.reason}` : ''}</span><span className="text-slate-400">{x.user_name || 'system'} · {fmt(x.at)}</span></li>
        ))}{!d.ai_log.length && <li className="py-2 text-slate-500">No AI requests yet.</li>}</ul>
      </Section>
    </div>
  );
};
